import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

// ── Thunks ────────────────────────────────────────────────────────────────────

export const fetchCatalogs = createAsyncThunk(
  'catalogs/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.catalogs.getAll(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCatalogStats = createAsyncThunk(
  'catalogs/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.catalogs.getStats();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCatalogById = createAsyncThunk(
  'catalogs/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.catalogs.getById(id);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCatalog = createAsyncThunk(
  'catalogs/create',
  async (catalogData, { rejectWithValue }) => {
    try {
      const response = await apiService.catalogs.create(catalogData);
      return { data: response.data.data, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Exchanges the OAuth `code` returned by the Meta permission dialog for an access token
export const exchangeCatalogPermissionCode = createAsyncThunk(
  'catalogs/exchangeCode',
  async (code, { rejectWithValue }) => {
    try {
      const response = await apiService.catalogs.exchangeCode({ code });
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


export const updateCatalog = createAsyncThunk(
  'catalogs/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.catalogs.update(id, data);
      return { data: response.data.data, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const setActiveCatalog = createAsyncThunk(
  'catalogs/setActive',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.catalogs.setActive(id);
      return { data: response.data.data, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteCatalog = createAsyncThunk(
  'catalogs/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.catalogs.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const catalogsSlice = createSlice({
  name: 'catalogs',
  initialState: {
    items: [],
    selectedCatalog: null,
    stats: null,
    loading: false,
    statsLoading: false,
    error: null,
    pagination: { page: 1, limit: 20, total: 0, pages: 1 },
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedCatalog: (state, action) => {
      state.selectedCatalog = action.payload;
    },
  },
  extraReducers: (builder) => {
    // fetchCatalogs
    builder
      .addCase(fetchCatalogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCatalogs.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || [];
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchCatalogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // fetchCatalogStats
    builder
      .addCase(fetchCatalogStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchCatalogStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchCatalogStats.rejected, (state) => {
        state.statsLoading = false;
      });

    // fetchCatalogById
    builder
      .addCase(fetchCatalogById.fulfilled, (state, action) => {
        state.selectedCatalog = action.payload;
      });

    // createCatalog
    builder
      .addCase(createCatalog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCatalog.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.data) {
          state.items.unshift(action.payload.data);
          if (state.stats) state.stats.total += 1;
        }
      })
      .addCase(createCatalog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // updateCatalog
    builder
      .addCase(updateCatalog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCatalog.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.data;
        if (updated) {
          const idx = state.items.findIndex((c) => c._id === updated._id);
          if (idx !== -1) state.items[idx] = updated;
          if (state.selectedCatalog?._id === updated._id) {
            state.selectedCatalog = updated;
          }
        }
      })
      .addCase(updateCatalog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // setActiveCatalog
    builder
      .addCase(setActiveCatalog.fulfilled, (state, action) => {
        const activatedId = action.payload.data?._id;
        // Deactivate all, then activate the chosen one
        state.items = state.items.map((c) => ({
          ...c,
          isActive: c._id === activatedId,
        }));
        if (state.selectedCatalog) {
          state.selectedCatalog = {
            ...state.selectedCatalog,
            isActive: state.selectedCatalog._id === activatedId,
          };
        }
        if (state.stats) {
          state.stats.activeCatalog = action.payload.data || null;
        }
      });

    // deleteCatalog
    builder
      .addCase(deleteCatalog.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCatalog.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((c) => c._id !== action.payload);
        if (state.selectedCatalog?._id === action.payload) {
          state.selectedCatalog = null;
        }
        if (state.stats) state.stats.total = Math.max(0, state.stats.total - 1);
      })
      .addCase(deleteCatalog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setSelectedCatalog } = catalogsSlice.actions;
export default catalogsSlice.reducer;
